/**
 * Kokpit — ZIYARET_TAKIP.md §5.1. KATMAN 1 (gün özeti).
 *
 * KPI şeridi + temsilci performans tablosu + 4 grafik + kural motorunun uyarıları.
 * Tablo satırına tıklanınca Ziyaret Listesi sekmesine o temsilci filtreli geçilir.
 */

import { useMemo } from 'react';
import {
  Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
  CartesianGrid, Legend,
} from 'recharts';
import {
  fmtMinutes, fmtMoney, fmtNum, fmtPct, fmtTime,
  STATUS_LABELS, STATUS_PILL, WORK_TYPE_LABELS,
} from './shared';
import { IcAlert, IcExcel } from './icons';

const DONUT_COLORS = ['var(--m-green)', 'var(--m-red)', 'var(--m-gray)'];

export function CockpitTab({ dashboard, days, alerts, loading, onPickRepresentative, onExcel }) {
  const kpi = dashboard?.kpi;
  const rows = days?.items || [];

  const donutData = useMemo(
    () => (dashboard?.successDonut || []).filter((d) => d.value > 0),
    [dashboard],
  );

  const hourly = useMemo(
    // 08:00–20:00 bandı: dışı saha saati değil, gösterilse grafik boş boşlukla dolar.
    () => (dashboard?.hourlyActivity || []).filter((h) => h.hour >= 6 && h.hour <= 22),
    [dashboard],
  );

  const comparison = useMemo(
    () => (dashboard?.representativeComparison || []).slice(0, 12),
    [dashboard],
  );

  return (
    <div className="pane on">
      {/* ── KPI şeridi ───────────────────────────────────────────────── */}
      <div className="stat c6" style={{ marginBottom: 14 }}>
        <Kpi cls="c-green" label="Sahadaki Temsilci"
          value={kpi ? `${kpi.activeRepresentatives}` : '—'}
          small={kpi ? `/${kpi.totalRepresentatives}` : null}
          meta={kpi && kpi.totalRepresentatives > kpi.activeRepresentatives
            ? `${kpi.totalRepresentatives - kpi.activeRepresentatives} kişi güne başlamadı`
            : 'Tümü sahada'} />
        <Kpi label="Planlanan Nokta" value={kpi ? fmtNum(kpi.plannedPointCount) : '—'}
          meta={`${rows.length} gün kaydı`} />
        <Kpi label="Gerçekleşen Ziyaret" value={kpi ? fmtNum(kpi.visitCount) : '—'}
          small={kpi?.realizationPct != null ? fmtPct(kpi.realizationPct) : null}
          meta="rut gerçekleşme oranı" />
        <Kpi cls="c-green" label="Başarılı Ziyaret" value={kpi ? fmtNum(kpi.successVisitCount) : '—'}
          small={kpi?.successPct != null ? fmtPct(kpi.successPct) : null}
          meta="belge/tahsilat çıkan ziyaret" />
        <Kpi cls="c-purple" label="Ort. Ziyaret Süresi"
          value={kpi?.avgVisitMin != null ? String(kpi.avgVisitMin) : '—'} small="dk"
          meta={kpi ? `toplam ${fmtMinutes(kpi.totalVisitMin)}` : null} />
        <Kpi cls="c-amber" label="Zamana Uymama" value={kpi ? fmtNum(kpi.timeViolationCount) : '—'}
          meta={kpi && kpi.visitCount > 0
            ? `ziyaretlerin ${fmtPct((kpi.timeViolationCount * 100) / kpi.visitCount)}'i`
            : 'plan saati tanımlı değil'} />
      </div>

      {/* ── Katman açıklaması ────────────────────────────────────────── */}
      <div className="alert info m" style={{ marginBottom: 14 }}>
        <div>
          <b>Bu ekran üç katmanlıdır.</b> Kokpit = <b>gün özeti</b> · Ziyaret Listesi = <b>ziyaret</b> ·
          Saha İzleme = <b>hareket logu</b>. Bir satıra tıklayınca bir alt katmana inersiniz.
        </div>
      </div>

      {/* ── Temsilci performans tablosu ──────────────────────────────── */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="sec-h" style={{ padding: '14px 16px 0', marginBottom: 10 }}>
          <div className="ico"><IcAlert size={18} /></div>
          <div>
            <b>Temsilci Performansı — Gün Özeti</b>
            <span className="sub" style={{ display: 'block' }}>
              {rows.length} satır · her satır bir temsilcinin bir günü
            </span>
          </div>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn sec sm" onClick={onExcel}>
            <IcExcel size={15} />Excel
          </button>
        </div>

        <div className="dg-wrap">
          <table className="dg">
            <thead>
              <tr>
                <th>Temsilci</th><th>Distribütör</th><th>Tip</th>
                <th>Güne Başlama</th><th>Günü Bitirme</th><th className="r">Çalışma</th>
                <th className="r">Rut Nokta</th><th className="r">Gidilen</th><th className="r">Ziyaret</th>
                <th className="r">Başarılı</th><th className="r">Başarısız</th>
                <th className="r">Gerç. %</th><th className="r">Başarı %</th><th className="r">Ort. Süre</th>
                <th className="r">Sipariş ₺</th><th className="r">Tahsilat ₺</th>
                <th className="r">KM</th><th className="r">Z.Uymama</th><th>Durum</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={19} style={{ textAlign: 'center', padding: 24 }}>Yükleniyor…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={19} style={{ textAlign: 'center', padding: 24, color: 'var(--t3)' }}>
                  Seçilen aralıkta gün kaydı yok.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="clk" onClick={() => onPickRepresentative(r)}>
                  <td>
                    <b>{r.representativeName || '—'}</b>
                    {r.representativeCode && <span className="sub"> · {r.representativeCode}</span>}
                  </td>
                  <td>{r.vendorName || '—'}</td>
                  <td>{r.workType || '—'}</td>
                  <td className="tnum">{fmtTime(r.startedAt)}</td>
                  <td className="tnum">{fmtTime(r.endedAt)}</td>
                  <td className="r tnum">{fmtMinutes(r.totalWorkMin)}</td>
                  <td className="r tnum">{fmtNum(r.plannedPointCount)}</td>
                  <td className="r tnum">{fmtNum(r.visitedPointCount)}</td>
                  <td className="r tnum">{fmtNum(r.visitCount)}</td>
                  <td className="r tnum txt-g">{fmtNum(r.successVisitCount)}</td>
                  <td className="r tnum txt-r">{fmtNum(r.failVisitCount)}</td>
                  <td className="r tnum">{fmtPct(r.realizationPct)}</td>
                  <td className="r tnum">{fmtPct(r.successPct)}</td>
                  <td className="r tnum">{r.avgVisitMin != null ? `${r.avgVisitMin} dk` : '—'}</td>
                  <td className="r tnum">{fmtMoney(r.orderAmount)}</td>
                  <td className="r tnum">{fmtMoney(r.collectionAmount)}</td>
                  <td className="r tnum">{fmtMoney(r.totalKm, 0)}</td>
                  <td className="r tnum">{fmtNum(r.timeViolationCount)}</td>
                  <td>
                    <span className={`pill ${STATUS_PILL[r.status] || 'gr'}`}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && days?.totals && (
              <tfoot>
                <tr>
                  <td colSpan={6}><b>Toplam ({days.totals.representativeCount} temsilci)</b></td>
                  <td className="r tnum"><b>{fmtNum(days.totals.plannedPointCount)}</b></td>
                  <td />
                  <td className="r tnum"><b>{fmtNum(days.totals.visitCount)}</b></td>
                  <td className="r tnum"><b>{fmtNum(days.totals.successVisitCount)}</b></td>
                  <td colSpan={4} />
                  <td className="r tnum"><b>{fmtMoney(days.totals.orderAmount)}</b></td>
                  <td className="r tnum"><b>{fmtMoney(days.totals.collectionAmount)}</b></td>
                  <td className="r tnum"><b>{fmtMoney(days.totals.totalKm, 0)}</b></td>
                  <td className="r tnum"><b>{fmtNum(days.totals.timeViolationCount)}</b></td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── Grafikler (2×2) ──────────────────────────────────────────── */}
      <div className="grid2" style={{ marginBottom: 14 }}>
        <ChartCard title="Ziyaret Başarı Performansı" sub="Başarılı / Başarısız / Gidilmeyen">
          {donutData.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donutData} dataKey="value" nameKey="label"
                  innerRadius="52%" outerRadius="78%" paddingAngle={2}>
                  {donutData.map((d, i) => <Cell key={d.key} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [fmtNum(v), n]} />
                <Legend verticalAlign="bottom" height={24} iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Saatlik Hareket Yoğunluğu" sub="Terminalde yapılan hareket adedi — saat bandı">
          {hourly.every((h) => h.total === 0) ? <Empty /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourly} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.35} />
                <XAxis dataKey="hour" tickFormatter={(h) => `${String(h).padStart(2, '0')}`}
                  tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip labelFormatter={(h) => `${String(h).padStart(2, '0')}:00`} />
                <Legend verticalAlign="bottom" height={22} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="visit" name="Ziyaret" stackId="a" fill="var(--m-green)" />
                <Bar dataKey="document" name="Belge" stackId="a" fill="var(--m-blue)" />
                <Bar dataKey="collection" name="Tahsilat" stackId="a" fill="var(--m-purple)" />
                <Bar dataKey="survey" name="Anket" stackId="a" fill="var(--m-teal)" />
                <Bar dataKey="other" name="Diğer" stackId="a" fill="var(--m-gray)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Ziyaret Süresi Dağılımı" sub="0-2 dk kutusu &quot;kapıdan geçiş&quot; şüphesidir">
          {(dashboard?.durationHistogram || []).every((d) => d.count === 0) ? <Empty /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dashboard.durationHistogram} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.35} />
                <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip formatter={(v) => [fmtNum(v), 'Ziyaret']} />
                <Bar dataKey="count" name="Ziyaret" radius={[4, 4, 0, 0]}>
                  {dashboard.durationHistogram.map((d) => (
                    <Cell key={d.bucket} fill={d.suspicious ? 'var(--m-red)' : 'var(--m-blue)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Temsilci Karşılaştırma" sub="Başarı yüzdesine göre sıralı">
          {comparison.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparison} layout="vertical" margin={{ top: 4, right: 20, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.35} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="representativeName" width={112} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [`%${v}`, 'Başarı']} />
                <Bar dataKey="successPct" name="Başarı %" fill="var(--m-purple)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* ── Dikkat Gerektirenler ─────────────────────────────────────── */}
      <div className="card">
        <div className="sec-h" style={{ padding: '14px 16px 12px', marginBottom: 0 }}>
          <div className="ico" style={{ background: 'rgba(226,68,92,.13)', color: 'var(--m-red)' }}>
            <IcAlert size={18} />
          </div>
          <div>
            <b>Dikkat Gerektirenler</b>
            <span className="sub" style={{ display: 'block' }}>Kural motorunun ürettiği uyarılar</span>
          </div>
        </div>
        {(alerts || []).length === 0 && (
          <div className="alert-row" style={{ color: 'var(--t3)', fontSize: 12.5 }}>
            Seçilen aralıkta uyarı üretilmedi.
          </div>
        )}
        {(alerts || []).map((a) => (
          <div className="alert-row" key={a.type}>
            <div className={`aic ${a.severity === 'red' ? 'rd' : 'am'}`}><IcAlert size={16} /></div>
            <div className="info">
              <b>{a.title}</b>
              <div className="m">{a.detail || '—'}</div>
            </div>
            <span className={`pill ${a.severity === 'red' ? 'r' : 'a'}`}>{a.count} {a.unit}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi({ cls = '', label, value, small, meta }) {
  return (
    <div className={`scard ${cls}`}>
      <div className="l">{label}</div>
      <div className="v">{value}{small && <small>{small}</small>}</div>
      {meta && <div className="m">{meta}</div>}
    </div>
  );
}

function ChartCard({ title, sub, children }) {
  return (
    <div className="card">
      <div className="sec-h" style={{ padding: '13px 16px 0', marginBottom: 4 }}>
        <div>
          <b style={{ fontSize: 13 }}>{title}</b>
          <span className="sub" style={{ display: 'block' }}>{sub}</span>
        </div>
      </div>
      <div className="chartbox">{children}</div>
    </div>
  );
}

function Empty() {
  return (
    <div style={{
      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--t3)', fontSize: 12.5,
    }}>
      Veri yok
    </div>
  );
}

export { WORK_TYPE_LABELS };
